import { useEffect, useState } from "react";
import api from "@/lib/api";

/* Makes Fitamingo installable on phones and turns on push notifications.
   iPhone: works once the app is added to the Home Screen (iOS 16.4+).
   Android / desktop Chrome: the browser offers "Install" and push works in the browser too. */

let installEvent = null;
const listeners = new Set();
const emit = () => listeners.forEach((f) => f());

export function registerServiceWorker() {
  if (typeof window === "undefined" || !("serviceWorker" in navigator)) return;
  if (process.env.NODE_ENV !== "production" && !window.__FM_SW_DEV) return;
  window.addEventListener("load", () => {
    navigator.serviceWorker.register("/sw.js").catch(() => {});
  });
  window.addEventListener("beforeinstallprompt", (e) => {
    e.preventDefault(); // we show our own "Install" button instead
    installEvent = e;
    emit();
  });
  window.addEventListener("appinstalled", () => {
    installEvent = null;
    emit();
  });
}

export const isStandalone = () =>
  typeof window !== "undefined" && (window.matchMedia?.("(display-mode: standalone)").matches || window.navigator.standalone === true);

export const isIos = () => typeof navigator !== "undefined" && /iphone|ipad|ipod/i.test(navigator.userAgent) && !window.MSStream;

const pushSupported = () => typeof window !== "undefined" && "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;

function urlBase64ToUint8Array(base64) {
  const padding = "=".repeat((4 - (base64.length % 4)) % 4);
  const raw = atob((base64 + padding).replace(/-/g, "+").replace(/_/g, "/"));
  return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
}

/** Ask permission, subscribe this device, and tell the server. Returns "on" | "denied" | "unsupported" | "off". */
export async function enablePush() {
  if (!pushSupported()) return "unsupported";
  const { data } = await api.get("/push/key");
  if (!data.enabled) return "unsupported";
  const perm = await Notification.requestPermission();
  if (perm !== "granted") return perm === "denied" ? "denied" : "off";
  const reg = (await navigator.serviceWorker.getRegistration()) || (await navigator.serviceWorker.register("/sw.js"));
  await navigator.serviceWorker.ready;
  let sub = await reg.pushManager.getSubscription();
  if (!sub) sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlBase64ToUint8Array(data.key) });
  const json = sub.toJSON();
  await api.post("/push/subscribe", { endpoint: json.endpoint, keys: json.keys });
  return "on";
}

export async function pushState() {
  if (!pushSupported()) return "unsupported";
  try {
    const { data } = await api.get("/push/key");
    if (!data.enabled) return "unsupported";
  } catch {
    return "unsupported";
  }
  if (Notification.permission === "denied") return "denied";
  const reg = await navigator.serviceWorker.getRegistration();
  const sub = reg && (await reg.pushManager.getSubscription());
  if (sub && Notification.permission === "granted") {
    // keep the server in step (e.g. after logging in as someone else on this phone)
    const json = sub.toJSON();
    api.post("/push/subscribe", { endpoint: json.endpoint, keys: json.keys }).catch(() => {});
    return "on";
  }
  return "off";
}

/** What the "Get the app" card should offer right now. */
export function useAppSetup() {
  const [, force] = useState(0);
  const [push, setPush] = useState("checking");
  useEffect(() => {
    const f = () => force((n) => n + 1);
    listeners.add(f);
    pushState().then(setPush);
    return () => listeners.delete(f);
  }, []);
  const standalone = isStandalone();
  return {
    standalone,
    canInstall: Boolean(installEvent) && !standalone,
    iosNeedsInstall: isIos() && !standalone,
    push,
    install: async () => {
      if (!installEvent) return;
      installEvent.prompt();
      await installEvent.userChoice.catch(() => {});
      installEvent = null;
      emit();
    },
    turnOnPush: async () => {
      try {
        setPush(await enablePush());
      } catch {
        setPush("off");
      }
    },
  };
}
